import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Mail, Save, Search, Send, X } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { SALES_AGENT_DIGEST_URL, SALES_AGENT_DIGEST_PREVIEW_URL, SALES_AGENT_DIGEST_TEST_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { useSalesAgentAccess } from "./shared";

/**
 * Sales Agent -> Daily email (trustedge sales_agent/digest.py).
 *
 * One email a working day, after the send time: how today's leads were handled,
 * what still needs doing, the team and the overall pipeline. All figures are
 * database counts. CRM users on the list get buttons (book a follow-up,
 * reassign, nudge the owner) that act as them; extra addresses get the report only.
 */

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_STYLE = {
    sent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    test: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

function Toggle({ on, onClick, disabled, label }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on} aria-label={label}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600", disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                on ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

function Row({ title, help, children }) {
    return (
        <div className="flex flex-col gap-2 border-b border-gray-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700/60">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                {help && <p className="max-w-xl text-xs text-gray-500 dark:text-gray-400">{help}</p>}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

const input = "h-8 rounded-md border border-gray-300 px-2 text-sm dark:border-gray-600 dark:bg-gray-800";

export default function DailyEmailSettings() {
    const { fetch, update, create } = useApiMethods();
    const navigate = useNavigate();
    const { canEditSettings: canEdit } = useSalesAgentAccess();
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [mailboxes, setMailboxes] = useState([]);
    const [people, setPeople] = useState([]);
    const [log, setLog] = useState([]);
    const [query, setQuery] = useState("");
    const [extra, setExtra] = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [preview, setPreview] = useState(null);

    const load = (res) => {
        setSaved(res.data); setDraft(res.data); setExtra((res.data.extra_emails || []).join("\n"));
        if (res.mailboxes) setMailboxes(res.mailboxes);
        if (res.people) setPeople(res.people);
        if (res.log) setLog(res.log);
    };

    useEffect(() => {
        fetch(SALES_AGENT_DIGEST_URL).then(res => {
            if (res?.status === "success") load(res);
            else ToastMsg("error", res?.message || "Could not load the daily email settings");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return people.filter(p => !q || `${p.name} ${p.email}`.toLowerCase().includes(q));
    }, [people, query]);

    if (!draft) return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;

    const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));
    const body = { ...draft, extra_emails: extra };
    const dirty = JSON.stringify(saved) !== JSON.stringify({ ...draft, extra_emails: saved.extra_emails })
        || (saved.extra_emails || []).join("\n") !== extra.trim();
    const toggleDay = (d) => set("working_days", draft.working_days.includes(d)
        ? draft.working_days.filter(x => x !== d) : [...draft.working_days, d].sort());
    const toggleUser = (id) => set("recipient_user_ids", draft.recipient_user_ids.includes(id)
        ? draft.recipient_user_ids.filter(x => x !== id) : [...draft.recipient_user_ids, id]);
    const chosen = draft.recipient_user_ids.map(id => people.find(p => p.id === id)).filter(Boolean);
    const sender = mailboxes.find(m => m.id === draft.sender_account_id);

    const save = async () => {
        setSaving(true);
        try {
            const res = await update(SALES_AGENT_DIGEST_URL, body);
            if (res?.status === "success") { load(res); ToastMsg("success", "Saved"); }
            else ToastMsg("error", res?.message || "Could not save");
        } finally { setSaving(false); }
    };

    const showPreview = async () => {
        const res = await fetch(SALES_AGENT_DIGEST_PREVIEW_URL);
        if (res?.status === "success") setPreview(res);
        else ToastMsg("error", res?.message || "Could not build the preview");
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const res = await create(SALES_AGENT_DIGEST_TEST_URL, {});
            if (res?.status === "success") { ToastMsg("success", res.message); setLog(res.log || []); }
            else ToastMsg("error", res?.message || "Could not send the test");
        } finally { setTesting(false); }
    };

    return (
        <ContainerCard>
            <div className="mx-auto max-w-5xl space-y-6 p-1 sm:p-2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <button type="button" onClick={() => navigate("/sales-agent/settings")} aria-label="Back"
                            className="mt-0.5 rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <div>
                            <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                                <Mail className="h-4 w-4 text-primary" /> Daily email
                            </h1>
                            <p className="text-xs text-gray-500">Today's leads and how they were handled, what needs doing, the team and the pipeline. Every figure is counted from the CRM; no AI.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={showPreview}
                            className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                            <Eye className="h-4 w-4" /> Preview
                        </button>
                        {canEdit && (
                            <button type="button" onClick={sendTest} disabled={testing || dirty}
                                title={dirty ? "Save first" : "Send today's email to yourself now"}
                                className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                                <Send className="h-4 w-4" /> {testing ? "Sending…" : "Send test to me"}
                            </button>
                        )}
                        {canEdit && (
                            <button type="button" onClick={save} disabled={!dirty || saving}
                                className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white disabled:opacity-50">
                                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                    </div>
                </div>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Schedule</h2>
                    <Row title="Send the daily email" help="Once a day on working days, after the time below (IST). Checked every 5 minutes.">
                        <Toggle on={draft.is_enabled} disabled={!canEdit} label="Daily email" onClick={() => set("is_enabled", !draft.is_enabled)} />
                    </Row>
                    <Row title="Send time">
                        <input type="time" value={draft.send_time} disabled={!canEdit} onChange={e => set("send_time", e.target.value)} className={input} />
                    </Row>
                    <Row title="Working days">
                        <div className="flex flex-wrap gap-1">
                            {DAYS.map((d, i) => (
                                <button key={d} type="button" disabled={!canEdit} onClick={() => toggleDay(i)} aria-pressed={draft.working_days.includes(i)}
                                    className={cn("h-8 w-11 rounded-md border text-xs", draft.working_days.includes(i)
                                        ? "border-primary bg-primary/10 text-primary" : "border-gray-300 text-gray-500 dark:border-gray-600")}>{d}</button>
                            ))}
                        </div>
                    </Row>
                    <Row title="Send from" help={sender && !sender.can_send ? "This mailbox's SMTP is not verified yet (Email → Accounts)." : "A connected mailbox whose SMTP has been verified."}>
                        <select value={draft.sender_account_id ?? ""} disabled={!canEdit}
                            onChange={e => set("sender_account_id", e.target.value ? Number(e.target.value) : null)} className={cn(input, "w-64")}>
                            <option value="">Choose a mailbox</option>
                            {mailboxes.map(m => (
                                <option key={m.id} value={m.id} disabled={!m.can_send}>
                                    {m.email_address}{m.can_send ? "" : " (SMTP not verified)"}
                                </option>
                            ))}
                        </select>
                    </Row>
                </section>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Who gets it</h2>
                    <p className="mb-3 text-xs text-gray-500">CRM users get buttons to book a follow-up, reassign or nudge the owner, acting with their own permissions. It goes to the email on their CRM login.</p>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                                <Search className="h-3.5 w-3.5 text-gray-400" />
                                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search people"
                                    className="w-full bg-transparent text-sm outline-none" />
                            </div>
                            <div className="max-h-72 overflow-y-auto">
                                {shown.map(p => {
                                    const on = draft.recipient_user_ids.includes(p.id);
                                    return (
                                        <label key={p.id} className={cn("flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800", !p.email && "opacity-60")}>
                                            <input type="checkbox" checked={on} disabled={!canEdit || (!p.email && !on)} onChange={() => toggleUser(p.id)} />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-medium text-gray-900 dark:text-white">{p.name}</span>
                                                <span className="block truncate text-xs text-gray-500">{p.email || "No email on this login"}</span>
                                            </span>
                                            <span className="text-[10px] uppercase text-gray-400">{p.role.replace("_", " ")}</span>
                                        </label>
                                    );
                                })}
                                {shown.length === 0 && <p className="px-3 py-6 text-center text-xs text-gray-400">Nobody matches.</p>}
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">Selected ({chosen.length})</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {chosen.map(p => (
                                        <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                                            {p.name}
                                            {canEdit && <button type="button" onClick={() => toggleUser(p.id)} aria-label={`Remove ${p.name}`}><X className="h-3 w-3" /></button>}
                                        </span>
                                    ))}
                                    {chosen.length === 0 && <span className="text-xs text-gray-400">Nobody yet.</span>}
                                </div>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300" htmlFor="extra">Other addresses (report only, no buttons)</label>
                                <textarea id="extra" rows={4} value={extra} disabled={!canEdit} onChange={e => setExtra(e.target.value)}
                                    placeholder={"one per line, e.g.\nowner@emedhub.in"}
                                    className="w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800" />
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Links</h2>
                    <Row title="CRM address" help="Where 'Open' links and the dashboard button go.">
                        <input value={draft.app_url} disabled={!canEdit} onChange={e => set("app_url", e.target.value)} className={cn(input, "w-72")} />
                    </Row>
                    <Row title="API address" help="Serves the page the action buttons open.">
                        <input value={draft.api_url} disabled={!canEdit} onChange={e => set("api_url", e.target.value)} className={cn(input, "w-72")} />
                    </Row>
                    <Row title="Leads listed" help="Today's new leads shown in full; the rest are counted.">
                        <input type="number" min="5" max="200" value={draft.max_leads} disabled={!canEdit}
                            onChange={e => set("max_leads", Number(e.target.value))} className={cn(input, "w-24")} />
                    </Row>
                </section>

                <section>
                    <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Recent sends</h2>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full min-w-[36rem] text-xs">
                            <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500 dark:bg-gray-800">
                                <tr><th className="px-3 py-2">Day</th><th>To</th><th>Status</th><th>Sent</th><th className="pr-3">Actions taken</th></tr>
                            </thead>
                            <tbody>
                                {log.map(r => (
                                    <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{dayjs(r.date).format("ddd D MMM")}</td>
                                        <td className="py-2 text-gray-900 dark:text-white">{r.email}</td>
                                        <td className="py-2">
                                            <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase", STATUS_STYLE[r.status])}>{r.status}</span>
                                            {r.error && <span className="ml-2 text-red-500" title={r.error}>{r.error.slice(0, 60)}</span>}
                                        </td>
                                        <td className="py-2 text-gray-500">{r.sent_at ? dayjs(r.sent_at).format("h:mm A") : "—"}</td>
                                        <td className="py-2 pr-3 tabular-nums text-gray-700 dark:text-gray-300">{r.actions_taken}</td>
                                    </tr>
                                ))}
                                {log.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Nothing sent yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPreview(null)}>
                    <div className="flex h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{preview.subject}</p>
                                <p className="text-xs text-gray-500">As it stands now · goes to {preview.recipients} {preview.recipients === 1 ? "person" : "people"} · buttons are inactive here</p>
                            </div>
                            <button type="button" onClick={() => setPreview(null)} aria-label="Close" className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <iframe title="Daily email preview" srcDoc={preview.html} sandbox="" className="w-full flex-1 bg-white" />
                    </div>
                </div>
            )}
        </ContainerCard>
    );
}
